import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { FileText, Save, Send, Eye, Plus, Trash2, Goal, Square, Flag, Pencil, UserPlus, ClipboardCheck, PenLine, Lock, Unlock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  listEvents, listEventCategoriesForScoresheet, getMatchData, saveScoresheet, listScoresheets, getScoresheetById, getScoresheetEditToken, setScoresheetClosed,
} from "@/lib/api/scoresheets.functions";

import { openScoresheetPdf, scoresheetPdfBase64, type ScoresheetPdfData } from "@/lib/pdf/scoresheet-pdf";
import { toMediaUrl } from "@/lib/media-url";
import { sendPdfBase64ViaWhatsapp, sendWhatsappBroadcast } from "@/lib/api/whatsapp.functions";
import { Share2, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SignaturePad } from "@/components/shared/SignaturePad";
import { foulAlertMessage, isFutsalModality } from "@/lib/foul-alert";

export const Route = createFileRoute("/_authenticated/m/sumula-jogo")({
  component: SumulaJogoPage,
});

type Player = {
  student_id?: string | null;
  name: string;
  jersey_number?: string | null;
  goals: number;
  yellow_cards: number;
  red_cards: number;
  fouls: number;
  category?: string | null;
  is_above_category?: boolean;
  photo_url?: string | null;
  present?: boolean;
};

type ActionKind = "goal" | "card" | "foul";

function fmtDate(d?: string | null) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("pt-BR"); } catch { return d; }
}

function SumulaJogoPage() {
  const router = useRouter();
  const listEv = useServerFn(listEvents);
  const listCats = useServerFn(listEventCategoriesForScoresheet);
  const getMatch = useServerFn(getMatchData);
  const save = useServerFn(saveScoresheet);
  const listSs = useServerFn(listScoresheets);
  const getSs = useServerFn(getScoresheetById);
  const setClosedFn = useServerFn(setScoresheetClosed);
  const sendPdf = useServerFn(sendPdfBase64ViaWhatsapp);
  const sendBroadcast = useServerFn(sendWhatsappBroadcast);

  const [wpOpen, setWpOpen] = useState(false);
  const [wpPhone, setWpPhone] = useState("");
  const [wpName, setWpName] = useState("");
  const [wpSending, setWpSending] = useState(false);
  const [wpTargetId, setWpTargetId] = useState<string | null>(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPromptOpen, setPdfPromptOpen] = useState(false);

  const [shareSending, setShareSending] = useState(false);

  const [actionOpen, setActionOpen] = useState(false);
  const [refereeAlert, setRefereeAlert] = useState<string>("");
  const [actionKind, setActionKind] = useState<ActionKind>("goal");
  const [actionTeam, setActionTeam] = useState<"host" | "visitor">("host");
  const [actionPlayerIdx, setActionPlayerIdx] = useState<string>("");
  const [actionCard, setActionCard] = useState<"yellow" | "red">("yellow");
  const [actionSearch, setActionSearch] = useState("");
  const [modality, setModality] = useState("QUADRA");

  const [regOpen, setRegOpen] = useState(false);
  const [regTeam, setRegTeam] = useState<"host" | "visitor">("visitor");
  const [regName, setRegName] = useState("");
  const [regJersey, setRegJersey] = useState("");
  const [showSignatures, setShowSignatures] = useState(false);



  const [tab, setTab] = useState("criar");
  const [events, setEvents] = useState<any[]>([]);
  const [evStatus, setEvStatus] = useState<"all" | "futuro" | "andamento" | "encerrado" | "cancelado">("all");

  const [categories, setCategories] = useState<any[]>([]);
  const [evId, setEvId] = useState("");
  const [catId, setCatId] = useState("");
  const [match, setMatch] = useState<any>(null);
  const [tcName, setTcName] = useState("");
  const [tcLogo, setTcLogo] = useState<string | null>(null);

  const [id, setId] = useState<string | null>(null);
  const [refereeName, setRefereeName] = useState("");
  const [refereeReport, setRefereeReport] = useState("");
  const [refereePhone, setRefereePhone] = useState("");
  const [organizerName, setOrganizerName] = useState("");
  const [organizerPhone, setOrganizerPhone] = useState("");
  const [visitorCaptainName, setVisitorCaptainName] = useState("");
  const [visitorCaptainPhone, setVisitorCaptainPhone] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [host, setHost] = useState<Player[]>([]);
  const [visitor, setVisitor] = useState<Player[]>([]);
  const [sigReferee, setSigReferee] = useState<string | null>(null);
  const [sigHost, setSigHost] = useState<string | null>(null);
  const [sigVisitor, setSigVisitor] = useState<string | null>(null);
  const [signaturesEpoch, setSignaturesEpoch] = useState(0);
  const [closedAt, setClosedAt] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [savedList, setSavedList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { listEv().then((r: any) => setEvents(r.events)).catch(() => {}); }, [listEv]);
  useEffect(() => { listSs().then((r: any) => setSavedList(r.scoresheets)).catch(() => {}); }, [listSs, tab]);

  useEffect(() => {
    if (!evId) { setCategories([]); setCatId(""); return; }
    listCats({ data: { championship_id: evId } }).then((r: any) => {
      const cats = r.categories ?? [];
      setCategories(cats);
      if (cats.length > 0) {
        setCatId((prev) => (prev && cats.some((c: any) => c.id === prev) ? prev : cats[0].id));
      } else {
        setCatId("");
      }
    });
  }, [evId, listCats]);

  useEffect(() => {
    if (!catId) { setMatch(null); return; }
    getMatch({ data: { event_category_id: catId } }).then((r: any) => {
      setMatch(r.summon); setTcName(r.tcName);
      void toMediaUrl((r as any).tcLogo ?? null).then(setTcLogo);
      const players: Player[] = (r.hostPlayers || []).map((p: any) => ({
        student_id: p.id, name: p.name, jersey_number: p.jersey_number ?? "",
        goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0,
        category: p.category ?? null, is_above_category: !!p.is_above_category,
        photo_url: p.photo_url ?? null,
      }));
      if (r.scoresheet) {
        setId(r.scoresheet.id);
        setRefereeName(r.scoresheet.referee_name ?? "");
        setRefereeReport(r.scoresheet.referee_report ?? "");
        setRefereePhone(r.scoresheet.referee_phone ?? "");
        setOrganizerName(r.scoresheet.organizer_name || r.organizerNameSuggestion || "");
        setOrganizerPhone(r.scoresheet.organizer_phone || r.organizerPhoneSuggestion || "");
        setVisitorCaptainName(r.scoresheet.visitor_captain_name || r.visitorTcNameSuggestion || "");
        setVisitorCaptainPhone(r.scoresheet.visitor_captain_phone || r.visitorTcPhoneSuggestion || "");
        setVisitorName(r.scoresheet.visitor_team_name ?? "");
        const savedHost = (r.scoresheet.host_players ?? []) as unknown as Player[];
        const savedVis = (r.scoresheet.visitor_players ?? []) as unknown as Player[];
        setHost(savedHost.length ? savedHost : players);
        setVisitor(savedVis);
        setSigReferee(r.scoresheet.signature_referee ?? null);
        setSigHost(r.scoresheet.signature_host_captain ?? null);
        setSigVisitor(r.scoresheet.signature_visitor_captain ?? null);
        setSignaturesEpoch((n) => n + 1);
        setClosedAt(r.scoresheet.closed_at ?? null);
        setModality(r.scoresheet.modality ?? "QUADRA");
      } else {
        setClosedAt(null);
        setId(null);
        setRefereeName(""); setRefereeReport("");
        setRefereePhone("");
        setOrganizerName(r.organizerNameSuggestion || "");
        setOrganizerPhone(r.organizerPhoneSuggestion || "");
        setVisitorCaptainName(r.visitorTcNameSuggestion || "");
        setVisitorCaptainPhone(r.visitorTcPhoneSuggestion || "");
        setVisitorName(
          (r as any).selfMatch
            ? `${r.tcName ?? ""} — Time Visitante`.trim()
            : (r.summon?.opponent ?? ""),
        );
        const visitors: Player[] = ((r as any).visitorAthletes || []).map((a: any) => ({
          student_id: null, name: a.name, jersey_number: a.jersey_number ? String(a.jersey_number) : "",
          goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0,
          category: a.category ?? null, is_above_category: !!a.above_category,
          photo_url: a.avatar_url ?? null,
        }));

        setHost(players); setVisitor(visitors);
        setSigReferee(null); setSigHost(null); setSigVisitor(null);
        setSignaturesEpoch((n) => n + 1);
      }
    });
  }, [catId, getMatch]);

  const champ = useMemo(() => events.find((e) => e.id === evId), [events, evId]);

  // Situação do evento (para o filtro da súmula)
  const eventStatusOf = (e: any): "futuro" | "andamento" | "encerrado" | "cancelado" => {
    if ((e?.status ?? "").toLowerCase() === "cancelado") return "cancelado";
    const today = new Date().toISOString().slice(0, 10);
    const start = e?.start_date ?? null;
    const end = e?.end_date ?? e?.start_date ?? null;
    if (!start) return "andamento";
    if (start > today) return "futuro";
    if (end && end < today) return "encerrado";
    return "andamento";
  };
  const STATUS_LABEL: Record<string, string> = {
    futuro: "🔜 Futuro", andamento: "▶️ Em andamento", encerrado: "🏁 Encerrado", cancelado: "⛔ Cancelado",
  };
  const filteredEvents = useMemo(
    () => (evStatus === "all" ? events : events.filter((e) => eventStatusOf(e) === evStatus)),
    [events, evStatus],
  );


  const updHost = (i: number, k: keyof Player, v: any) =>
    setHost((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const updVis = (i: number, k: keyof Player, v: any) =>
    setVisitor((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const openAction = (kind: ActionKind) => {
    setActionKind(kind);
    setActionTeam("host");
    setActionPlayerIdx("");
    setActionSearch("");
    setActionCard("yellow");
    setActionOpen(true);
  };

  const applyAction = () => {
    // For fouls, athlete is optional — a foul without a picked athlete is
    // recorded as a "team foul" on a synthetic "— Equipe —" row.
    if (actionPlayerIdx === "" && actionKind !== "foul") {
      return toast.error("Selecione o atleta.");
    }
    const setFn = actionTeam === "host" ? setHost : setVisitor;

    if (actionKind === "foul" && actionPlayerIdx === "") {
      const teamName = actionTeam === "host" ? "Mandante" : "Visitante";
      const prevArr = actionTeam === "host" ? host : visitor;
      const prevTotal = prevArr.reduce((s, p) => s + (p.fouls || 0), 0);
      setFn((prev) => {
        const teamIdx = prev.findIndex((p) => !p.student_id && p.name === "— Equipe —");
        if (teamIdx >= 0) {
          return prev.map((p, i) => i === teamIdx ? { ...p, fouls: (p.fouls || 0) + 1 } : p);
        }
        return [...prev, { name: "— Equipe —", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 1 }];
      });
      toast.success(`Falta da equipe registrada (${teamName}).`);
      {
        const msg = foulAlertMessage(teamName, prevTotal + 1, modality);
        if (msg) setRefereeAlert(msg);
      }
      setActionOpen(false);
      return;
    }

    const idx = Number(actionPlayerIdx);
    const prevArr = actionTeam === "host" ? host : visitor;
    const teamName = actionTeam === "host" ? "Mandante" : "Visitante";
    const prevTeamFouls = prevArr.reduce((s, p) => s + (p.fouls || 0), 0);
    setFn((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      if (actionKind === "goal") return { ...p, goals: (p.goals || 0) + 1 };
      if (actionKind === "foul") return { ...p, fouls: (p.fouls || 0) + 1 };
      if (actionCard === "yellow") {
        const y = (p.yellow_cards || 0) + 1;
        if (y >= 2) {
          setRefereeAlert(`${p.name}: 2º cartão amarelo. Aplique cartão VERMELHO — avise o árbitro.`);
        }
        return { ...p, yellow_cards: y };
      }
      return { ...p, red_cards: (p.red_cards || 0) + 1 };
    }));
    if (actionKind === "foul") {
      const msg = foulAlertMessage(teamName, prevTeamFouls + 1, modality);
      if (msg) setRefereeAlert(msg);
    }
    const teamLabel = actionTeam === "host" ? "Mandante" : "Visitante";
    const playersRef = actionTeam === "host" ? host : visitor;
    const name = playersRef[idx]?.name ?? "";
    const kindLabel = actionKind === "goal" ? "Gol" : actionKind === "foul" ? "Falta" : (actionCard === "yellow" ? "Cartão amarelo" : "Cartão vermelho");
    toast.success(`${kindLabel} registrado: ${name} (${teamLabel}).`);
    setActionOpen(false);
  };

  const applyRegister = () => {
    const n = regName.trim();
    if (!n) return toast.error("Informe o nome do atleta.");
    const newP: Player = {
      name: n, jersey_number: regJersey.trim() || null,
      goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0, present: true,
    };
    if (regTeam === "host") setHost((p) => [...p, newP]);
    else setVisitor((p) => [...p, newP]);
    toast.success(`Atleta cadastrado (${regTeam === "host" ? "Mandante" : "Visitante"}).`);
    setRegName(""); setRegJersey(""); setRegOpen(false);
  };





  const handleSave = async () => {
    if (!catId) return toast.error("Selecione uma partida");
    setLoading(true);
    try {
      const res = await save({ data: {
        id, summon_id: null, event_category_id: catId, championship_id: evId || null,
        modality,
        footwear: "Chuteira sem trava quando for salão",
        match_time: "2x de 20 min",
        goalkeeper_above: "Sim", female_above: "Sim",
        athletes_info: "5 linha (Max inscritos: 15)",
        rules_text: "Futsal: Amarelo = Advertência (2+1 Vermelho). Vermelho = Expulsão (2 min fora ou até sofrer gol, entra outro). 6ª Falta = Tiro Livre Direto.",
        referee_name: refereeName, referee_report: refereeReport,
        referee_phone: refereePhone,
        organizer_name: organizerName, organizer_phone: organizerPhone,
        visitor_captain_name: visitorCaptainName, visitor_captain_phone: visitorCaptainPhone,
        host_players: host, visitor_team_name: visitorName, visitor_players: visitor,
        signature_referee: sigReferee, signature_host_captain: sigHost, signature_visitor_captain: sigVisitor,
      }});
      setId(res.id);
      toast.success("Súmula salva com sucesso!");
      router.invalidate();
      setPdfPromptOpen(true);

    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar");
    } finally { setLoading(false); }
  };

  const handleCloseGame = async () => {
    setClosing(true);
    try {
      let sheetId = id;
      if (!sheetId || !closedAt) {
        await handleSave();
      }
      sheetId = sheetId ?? id;
      if (!sheetId) throw new Error("Salve a súmula antes de encerrar o jogo.");
      await setClosedFn({ data: { id: sheetId, closed: true } });
      setClosedAt(new Date().toISOString());
      setCloseOpen(false);
      toast.success("Jogo encerrado. A súmula não pode mais ser alterada.");
      router.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao encerrar o jogo");
    } finally { setClosing(false); }
  };

  const handleReopen = async () => {
    if (!id) return;
    setClosing(true);
    try {
      await setClosedFn({ data: { id, closed: false } });
      setClosedAt(null);
      setReopenOpen(false);
      toast.success("Súmula reaberta para edição.");
      router.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao reabrir a súmula");
    } finally { setClosing(false); }
  };

  const currentPdfData = (): ScoresheetPdfData => ({
    tcName, tcLogoUrl: tcLogo, champName: champ?.name ?? "", champCategory: champ?.category ?? "",
    matchDate: fmtDate(match?.match_date), matchTime: match?.match_time?.slice(0, 5) ?? "-",
    location: match?.location ?? "-",
    visitorName, host: host as any, visitor: visitor as any,
    refereeName, refereeReport, modality,
    hostCaptainName: organizerName || null,
    visitorCaptainName: visitorCaptainName || null,
    signatureReferee: sigReferee,
    signatureHostCaptain: sigHost,
    signatureVisitorCaptain: sigVisitor,
  });

  const savedToPdfData = async (id: string): Promise<{ data: ScoresheetPdfData; fileName: string }> => {
    const r: any = await getSs({ data: { id } });
    const s = r.scoresheet;
    const data: ScoresheetPdfData = {
      tcName: r.tcName,
      tcLogoUrl: await toMediaUrl((r as any).tcLogo ?? null),
      champName: r.champName,
      champCategory: r.champCategory,
      matchDate: fmtDate(r.matchDate),
      matchTime: r.matchTime ? String(r.matchTime).slice(0, 5) : "-",
      location: r.location ?? "-",
      visitorName: r.visitorName || "",
      host: (s.host_players ?? []) as any,
      visitor: (s.visitor_players ?? []) as any,
      refereeName: s.referee_name ?? "",
      refereeReport: s.referee_report ?? "",
      modality: s.modality, footwear: s.footwear, matchDuration: s.match_time,
      goalkeeperAbove: s.goalkeeper_above, femaleAbove: s.female_above,
      athletesInfo: s.athletes_info, rulesText: s.rules_text,
      hostCaptainName: s.organizer_name ?? null,
      visitorCaptainName: s.visitor_captain_name ?? null,
      signatureReferee: s.signature_referee ?? null,
      signatureHostCaptain: s.signature_host_captain ?? null,
      signatureVisitorCaptain: s.signature_visitor_captain ?? null,
    };
    const fileName = `sumula-${(r.champName || "jogo").replace(/[^A-Za-z0-9]+/g, "_").toLowerCase()}.pdf`;
    return { data, fileName };
  };

  const handleViewSaved = async (id: string) => {
    try {
      const { data } = await savedToPdfData(id);
      await openScoresheetPdf(data);
    } catch (e: any) { toast.error(e?.message ?? "Erro ao gerar PDF"); }
  };

  const [editCatOpen, setEditCatOpen] = useState(false);
  const [editCatId, setEditCatId] = useState("");
  const [editCatEventId, setEditCatEventId] = useState("");
  const [editCatOptions, setEditCatOptions] = useState<any[]>([]);
  const [editCatLoading, setEditCatLoading] = useState(false);

  const handleEditSaved = async (sid: string) => {
    try {
      const r: any = await getSs({ data: { id: sid } });
      const s = r.scoresheet;
      const chId = s.championship_id ?? null;
      const ecId = s.event_category_id ?? null;
      setEditCatEventId(chId ?? "");
      setEditCatId(ecId ?? "");
      if (chId) {
        const cats: any = await listCats({ data: { championship_id: chId } });
        const options = cats?.categories ?? [];
        setEditCatOptions(options);
        setEditCatId(ecId && options.some((c: any) => c.id === ecId) ? ecId : options[0]?.id ?? "");
      } else {
        setEditCatOptions([]);
      }
      setEditCatOpen(true);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao carregar súmula.");
    }
  };

  const confirmEditSaved = (_sid: string) => {
    if (!editCatId) {
      toast.error("Selecione uma categoria.");
      return;
    }
    setEditCatOpen(false);
    setTab("criar");
    setEvId(editCatEventId);
    setCatId(editCatId);
    toast.success("Súmula carregada para edição.");
  };

  const getTokenFn = useServerFn(getScoresheetEditToken);
  const tokenCache = useMemo(() => new Map<string, string>(), []);
  const resolveEditLink = async (sid: string): Promise<string> => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    let tok = tokenCache.get(sid);
    if (!tok) {
      const r: any = await getTokenFn({ data: { id: sid } });
      tok = r.token;
      if (tok) tokenCache.set(sid, tok);
    }
    return `${origin}/sumula/${sid}?t=${tok}`;
  };
  const [shareLink, setShareLink] = useState("");


  const handleShareLink = async () => {
    if (!id) return toast.error("Salve a súmula antes de compartilhar.");
    const recipients = [
      { name: refereeName || "Árbitro", phone: refereePhone },
      { name: organizerName || "Organizador", phone: organizerPhone },
      { name: visitorCaptainName || "Centro de Treinamento Visitante", phone: visitorCaptainPhone },
    ].filter((r) => (r.phone || "").replace(/\D/g, "").length >= 10);
    if (recipients.length === 0) return toast.error("Preencha ao menos um telefone.");
    const link = await resolveEditLink(id);
    const message =
      `Olá {nome}! 👋\n\n` +
      `Segue o link para preencher/editar a súmula do jogo *${champ?.name ?? ""}*` +
      (match?.match_date ? ` (${fmtDate(match.match_date)})` : "") +
      `:\n\n${link}\n\n` +
      `⚠ Este é um link *aberto*: quem tem o link pode editar a súmula.`;
    setShareSending(true);
    try {
      const res: any = await sendBroadcast({ data: { message, recipients } });
      toast.success(`Link enviado (${res.sent} sucesso, ${res.failed} falhas).`);
      setShareOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar link.");
    } finally { setShareSending(false); }
  };


  const handleSendPdfToWhatsapp = async () => {
    if (!wpPhone) return toast.error("Informe o telefone");
    setWpSending(true);
    try {
      let data: ScoresheetPdfData; let fileName: string;
      if (wpTargetId) {
        const built = await savedToPdfData(wpTargetId);
        data = built.data; fileName = built.fileName;
      } else {
        data = currentPdfData();
        fileName = `sumula-${(data.champName || "jogo").replace(/[^A-Za-z0-9]+/g, "_").toLowerCase()}.pdf`;
      }
      const base64 = await scoresheetPdfBase64(data);
      // Envio apenas da súmula com os dados do evento — sem link de edição.
      const caption = [
        `📄 *Súmula do jogo*`,
        data.champName ? `🏆 ${data.champName}` : "",
        data.champCategory ? `👥 Categoria: ${data.champCategory}` : "",
        data.matchDate ? `📅 ${data.matchDate}${data.matchTime ? ` às ${data.matchTime}` : ""}` : "",
        data.location ? `📍 ${data.location}` : "",
        `⚔ ${data.tcName || "Mandante"} x ${data.visitorName || "Visitante"}`,
      ].filter(Boolean).join("\n");
      await sendPdf({ data: {
        phone: wpPhone, recipientName: wpName, base64, fileName,
        caption,
      }});
      toast.success("PDF enviado por WhatsApp");
      setWpOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar");
    } finally { setWpSending(false); }
  };



  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <FileText className="h-7 w-7 text-primary" />
        <h1 className="text-3xl font-bold tracking-tight">Súmula do Jogo</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="criar">Criar / Preencher Súmula</TabsTrigger>
          <TabsTrigger value="salvas">Súmulas Salvas</TabsTrigger>
        </TabsList>

        <TabsContent value="criar" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Selecionar Partida</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Situação do evento</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {([
                    ["all", "Todos"],
                    ["futuro", STATUS_LABEL.futuro],
                    ["andamento", STATUS_LABEL.andamento],
                    ["encerrado", STATUS_LABEL.encerrado],
                    ["cancelado", STATUS_LABEL.cancelado],
                  ] as const).map(([v, label]) => (
                    <Button
                      key={v}
                      size="sm"
                      variant={evStatus === v ? "default" : "outline"}
                      onClick={() => setEvStatus(v as any)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Evento</Label>
                <Select value={evId} onValueChange={setEvId}>
                  <SelectTrigger><SelectValue placeholder="-- Selecione o Evento --" /></SelectTrigger>
                  <SelectContent>
                    {filteredEvents.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.start_date ? `(${fmtDate(e.start_date)})` : ""} · {STATUS_LABEL[eventStatusOf(e)]}
                      </SelectItem>
                    ))}
                    {filteredEvents.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nenhum evento nesta situação</div>}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Categoria / Campo</Label>
                <Select value={catId} onValueChange={setCatId} disabled={!evId}>
                  <SelectTrigger><SelectValue placeholder="-- Selecione a Categoria --" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c: any) => {
                      const label = `${c.category ?? champ?.category ?? "Categoria"} · ${c.field_name ?? "Campo"}${c.start_time ? ` · ${String(c.start_time).slice(0,5)}` : ""}`;
                      return <SelectItem key={c.id} value={c.id}>{label}</SelectItem>;
                    })}
                    {evId && categories.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma categoria/campo cadastrado neste evento</div>}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {match && (
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-center">SÚMULA DE JOGO - {champ?.name?.toUpperCase()}</CardTitle>
                <p className="text-center text-sm text-muted-foreground">
                  {tcName} | Data: {fmtDate(match.match_date)} | Campo: {match.location ?? "-"} | Categoria: {champ?.category ?? "-"} | Horário: {match.match_time?.slice(0,5) ?? "-"}
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="rounded-lg border bg-gradient-to-br from-slate-900 to-slate-700 text-white p-4">
                  <div className="grid grid-cols-3 items-center gap-2">
                    <div className="text-center">
                      <div className="text-xs uppercase opacity-80 truncate">{tcName || "Mandante"}</div>
                      <div className="text-5xl font-black tabular-nums">{host.reduce((a, p) => a + (Number(p.goals) || 0), 0)}</div>
                    </div>
                    <div className="text-center text-2xl font-bold opacity-70">×</div>
                    <div className="text-center">
                      <div className="text-xs uppercase opacity-80 truncate">{visitorName || "Visitante"}</div>
                      <div className="text-5xl font-black tabular-nums">{visitor.reduce((a, p) => a + (Number(p.goals) || 0), 0)}</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 border rounded-md p-3 bg-muted/20">
                  <span className="text-sm font-semibold self-center mr-2">Registrar:</span>

                  <Button size="lg" onClick={() => openAction("goal")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base px-6">
                    <Goal className="h-5 w-5 mr-2" />GOL
                  </Button>
                  <Button size="lg" onClick={() => openAction("card")}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-base px-6">
                    <Square className="h-5 w-5 mr-2" />CARTÕES
                  </Button>
                  <Button size="lg" onClick={() => openAction("foul")}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-base px-6">
                    <Flag className="h-5 w-5 mr-2" />FALTA
                  </Button>
                  <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 self-center"
                    onClick={() => { setRegTeam("visitor"); setRegName(""); setRegJersey(""); setRegOpen(true); }}>
                    <UserPlus className="h-4 w-4 mr-1" />Cadastrar Atleta
                  </Button>
                  <span className="text-xs text-muted-foreground self-center ml-auto flex items-center gap-1">
                    <ClipboardCheck className="h-3 w-3" /> Marque a presença na coluna "Pres."
                  </span>
                </div>


                <div className="grid md:grid-cols-2 gap-6">
                  <TeamTable
                    title={`${tcName?.toUpperCase()} (MANDANTE)`}
                    players={host} onChange={updHost}
                    onAdd={() => setHost([...host, { name: "", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0 }])}
                    onRemove={(i) => setHost(host.filter((_, idx) => idx !== i))}
                    showStudentAvatar
                  />
                  <div className="space-y-2">
                    <Input
                      placeholder="Nome do time visitante"
                      value={visitorName} onChange={(e) => setVisitorName(e.target.value)}
                      className="font-semibold"
                    />
                    <TeamTable
                      title="(VISITANTE)"
                      players={visitor} onChange={updVis}
                      onAdd={() => setVisitor([...visitor, { name: "", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0 }])}
                      onRemove={(i) => setVisitor(visitor.filter((_, idx) => idx !== i))}
                    />
                  </div>
                </div>


                <div className="border rounded-md p-4 bg-muted/30">
                  <h3 className="font-semibold mb-2">REGRAS ESTABELECIDAS</h3>
                  <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <b>Modalidade:</b>
                      <Select value={modality} onValueChange={setModality}>
                        <SelectTrigger className="h-8 w-[220px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="QUADRA">FUTEBOL DE SALÃO (QUADRA)</SelectItem>
                          <SelectItem value="SOCIETY">SOCIETY</SelectItem>
                          <SelectItem value="CAMPO">CAMPO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><b>Calçado:</b> Chuteira sem trava quando for salão</div>
                    <div><b>Tempo:</b> 2x de 20 min</div>
                    <div><b>Goleiro Acima:</b> Sim</div>
                    <div><b>Feminino Acima:</b> Sim</div>
                    <div><b>Atletas:</b> 5 linha (Max inscritos: 15)</div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {isFutsalModality(modality)
                      ? "Futsal: Amarelo = Advertência (2+1 Vermelho). Vermelho = Expulsão (2 min fora ou até sofrer gol, entra outro). 6ª Falta = Tiro Livre Direto."
                      : "Amarelo = Advertência (2+1 Vermelho). Vermelho = Expulsão. Faltas acumuladas apenas registradas nesta modalidade."}
                  </p>
                </div>

                <div>
                  <Label>Relatório do Árbitro</Label>
                  <Textarea
                    rows={4} placeholder="Ocorrências, lesões, comportamento das equipes..."
                    value={refereeReport} onChange={(e) => setRefereeReport(e.target.value)}
                  />
                </div>

                <div>
                  <Button variant="outline" size="sm" onClick={() => setShowSignatures((v) => !v)}>
                    <PenLine className="h-4 w-4 mr-2" />
                    {showSignatures ? "Ocultar assinaturas" : "Assinar súmula"}
                  </Button>
                </div>
                {showSignatures && (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Nome do Árbitro</Label>
                    <Input value={refereeName} onChange={(e) => setRefereeName(e.target.value)} />
                    <Label className="text-xs">WhatsApp do Árbitro</Label>
                    <Input value={refereePhone} onChange={(e) => setRefereePhone(e.target.value)} placeholder="(11) 99999-9999" />
                    <SignaturePad key={`sr-${signaturesEpoch}`} value={sigReferee} onChange={setSigReferee} label="Assinatura Árbitro" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome do Organizador</Label>
                    <Input value={organizerName} onChange={(e) => setOrganizerName(e.target.value)} />
                    <Label className="text-xs">WhatsApp do Organizador</Label>
                    <Input value={organizerPhone} onChange={(e) => setOrganizerPhone(e.target.value)} placeholder="(11) 99999-9999" />
                    <SignaturePad key={`sh-${signaturesEpoch}`} value={sigHost} onChange={setSigHost} label="Assinatura Capitão Mandante" />
                  </div>
                  <div className="space-y-2">
                    <Label>Centro de Treinamento Visitante</Label>
                    <Input value={visitorCaptainName} onChange={(e) => setVisitorCaptainName(e.target.value)} />
                    <Label className="text-xs">WhatsApp do Centro de Treinamento Visitante</Label>
                    <Input value={visitorCaptainPhone} onChange={(e) => setVisitorCaptainPhone(e.target.value)} placeholder="(11) 99999-9999" />
                    <SignaturePad key={`sv-${signaturesEpoch}`} value={sigVisitor} onChange={setSigVisitor} label="Assinatura CT Visitante" />
                  </div>
                </div>
                )}


                <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setCatId("")}>Fechar Súmula</Button>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-transform hover:scale-[1.03]"
                    onClick={() => openScoresheetPdf(currentPdfData())}>
                    <Eye className="h-4 w-4 mr-2" />Visualizar / PDF completo
                  </Button>
                  <Button variant="secondary" onClick={() => { setWpTargetId(null); setWpPhone(""); setWpName(""); setWpOpen(true); }}>
                    <Send className="h-4 w-4 mr-2" />Enviar PDF por WhatsApp
                  </Button>
                  <Button variant="secondary" disabled={!id}
                    onClick={async () => {
                      if (id) { try { setShareLink(await resolveEditLink(id)); } catch { setShareLink(""); } }
                      setShareOpen(true);
                    }}>
                    <Share2 className="h-4 w-4 mr-2" />Compartilhar link p/ edição
                  </Button>

                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleSave}
                    disabled={loading || !!closedAt}
                  >
                    <Save className="h-4 w-4 mr-2" />Salvar Súmula
                  </Button>
                  {closedAt ? (
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={!id || closing}
                      onClick={() => setReopenOpen(true)}
                    >
                      <Unlock className="h-4 w-4 mr-2" />Reabrir Súmula
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      disabled={!catId || closing}
                      onClick={() => setCloseOpen(true)}
                    >
                      <Lock className="h-4 w-4 mr-2" />Encerrar Jogo
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="salvas" className="space-y-4 mt-4">
          {savedList.length === 0 && (
            <div className="rounded-lg border bg-card p-10 text-center text-muted-foreground">
              Nenhuma súmula salva ainda.
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedList.map((s) => {
              const totalHost = (s.host_players ?? []).reduce((a: number, p: any) => a + (p.goals || 0), 0);
              const totalVis = (s.visitor_players ?? []).reduce((a: number, p: any) => a + (p.goals || 0), 0);
              return (
                <Card key={s.id}>
                  <CardHeader>
                    <CardTitle className="text-lg uppercase">{s.championships?.name ?? "Sem evento"}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {s.championships?.category ?? "-"} • {fmtDate(s.game_summons?.match_date)}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm"><b>Árbitro:</b> {s.referee_name || "N/A"}</p>
                    <p className="text-sm"><b>Gols Mandante:</b> {totalHost}</p>
                    <p className="text-sm"><b>Gols Visitante:</b> {totalVis}</p>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-transform hover:scale-[1.03]"
                        onClick={() => handleViewSaved(s.id)}>
                        <Eye className="h-4 w-4 mr-2" />Visualizar / PDF
                      </Button>
                      <Button variant="default" size="sm" className="flex-1" onClick={() => handleEditSaved(s.id)}>
                        <Pencil className="h-4 w-4 mr-2" />Editar
                      </Button>
                    </div>
                    <Button size="sm" className="w-full" onClick={() => { setWpTargetId(s.id); setWpPhone(""); setWpName(""); setWpOpen(true); }}>
                      <Send className="h-4 w-4 mr-2" />Enviar WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" className="w-full"
                      onClick={async () => {
                        try {
                          const link = await resolveEditLink(s.id);
                          navigator.clipboard.writeText(link);
                          toast.success("Link de edição copiado");
                        } catch (e: any) { toast.error(e?.message || "Erro"); }
                      }}>
                      <Copy className="h-4 w-4 mr-2" />Copiar link de edição
                    </Button>

                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={wpOpen} onOpenChange={setWpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enviar Súmula por WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Telefone (com DDD)</Label>
              <Input value={wpPhone} onChange={(e) => setWpPhone(e.target.value)} placeholder="(11) 99999-9999" />
            </div>
            <div>
              <Label>Nome do destinatário (opcional)</Label>
              <Input value={wpName} onChange={(e) => setWpName(e.target.value)} placeholder="Ex: Árbitro João" />
            </div>
            <p className="text-xs text-muted-foreground">O PDF completo da súmula será anexado como documento.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWpOpen(false)}>Cancelar</Button>
            <Button disabled={wpSending || !wpPhone} onClick={handleSendPdfToWhatsapp}>
              {wpSending ? "Enviando..." : "Enviar PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editCatOpen} onOpenChange={setEditCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolher categoria para edição</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Categoria / Campo</Label>
            <Select value={editCatId} onValueChange={setEditCatId} disabled={editCatOptions.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder="-- Selecione a Categoria --" />
              </SelectTrigger>
              <SelectContent>
                {editCatOptions.map((c: any) => {
                  const label = `${c.category ?? "Categoria"} · ${c.field_name ?? "Campo"}${c.start_time ? ` · ${String(c.start_time).slice(0,5)}` : ""}`;
                  return <SelectItem key={c.id} value={c.id}>{label}</SelectItem>;
                })}
                {editCatOptions.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma categoria disponível</div>}
              </SelectContent>
            </Select>
            {editCatOptions.length > 0 && editCatId && (
              <p className="text-xs text-muted-foreground">
                Categoria selecionada: {editCatOptions.find((c: any) => c.id === editCatId)?.category ?? "-"}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCatOpen(false)}>Cancelar</Button>
            <Button onClick={() => confirmEditSaved(editCatId)} disabled={!editCatId || editCatLoading}>
              {editCatLoading ? "Carregando..." : "Abrir para editar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pdfPromptOpen} onOpenChange={setPdfPromptOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Baixar PDF da súmula?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            A súmula foi salva. Deseja gerar e baixar o PDF agora?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfPromptOpen(false)}>Agora não</Button>
            <Button onClick={async () => {
              setPdfPromptOpen(false);
              try {
                const sid = id;
                if (sid) {
                  const { data } = await savedToPdfData(sid);
                  await openScoresheetPdf(data);
                } else {
                  await openScoresheetPdf(currentPdfData());
                }
              } catch (e: any) { toast.error(e?.message ?? "Erro ao gerar PDF"); }
            }}>Baixar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Compartilhar link de edição</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Será enviado um WhatsApp para o Árbitro, o Organizador e o Centro de Treinamento Visitante
              com o link aberto para editar a súmula. Quem tiver o link poderá editar.
            </p>
            {id && shareLink && (
              <div className="rounded border bg-muted/40 p-2 text-xs break-all">
                {shareLink}
                <Button variant="ghost" size="sm" className="ml-2 h-6 px-2"
                  onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Link copiado"); }}>
                  <Copy className="h-3 w-3 mr-1" />Copiar
                </Button>
              </div>
            )}

            <ul className="text-xs space-y-1">
              <li>🧑‍⚖️ Árbitro: <b>{refereeName || "—"}</b> · {refereePhone || <span className="text-destructive">sem telefone</span>}</li>
              <li>👤 Organizador: <b>{organizerName || "—"}</b> · {organizerPhone || <span className="text-destructive">sem telefone</span>}</li>
              <li>🏟️ CT Visitante: <b>{visitorCaptainName || "—"}</b> · {visitorCaptainPhone || <span className="text-destructive">sem telefone</span>}</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cancelar</Button>
            <Button disabled={shareSending} onClick={handleShareLink}>
              {shareSending ? "Enviando..." : "Enviar link para os 3"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refereeAlert} onOpenChange={(o) => { if (!o) setRefereeAlert(""); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>⚠️ Avise o árbitro</DialogTitle></DialogHeader>
          <p className="text-sm py-2">{refereeAlert}</p>
          <DialogFooter>
            <Button onClick={() => setRefereeAlert("")}>OK, avisei o árbitro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cadastrar Atleta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipe</Label>
              <Select value={regTeam} onValueChange={(v) => setRegTeam(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">{tcName || "Mandante"} (Mandante)</SelectItem>
                  <SelectItem value="visitor">{visitorName || "Visitante"} (Visitante)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome do atleta *</Label>
              <Input value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Nome completo" autoFocus />
            </div>
            <div>
              <Label>Nº da camisa (opcional)</Label>
              <Input value={regJersey} onChange={(e) => setRegJersey(e.target.value)} placeholder="Ex: 10" />
            </div>
            <p className="text-xs text-muted-foreground">Cadastro rápido para inscrever atletas direto na súmula.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancelar</Button>
            <Button onClick={applyRegister} disabled={!regName.trim()}>Cadastrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionKind === "goal" && "Registrar Gol"}
              {actionKind === "card" && "Registrar Cartão"}
              {actionKind === "foul" && "Registrar Falta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipe</Label>
              <Select value={actionTeam} onValueChange={(v) => { setActionTeam(v as any); setActionPlayerIdx(""); setActionSearch(""); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">{tcName || "Mandante"} (Mandante)</SelectItem>
                  <SelectItem value="visitor">{visitorName || "Visitante"} (Visitante)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Atleta</Label>
              <Input
                className="mb-2"
                placeholder="Buscar por nome ou número..."
                value={actionSearch}
                onChange={(e) => setActionSearch(e.target.value)}
              />
              <Select value={actionPlayerIdx} onValueChange={setActionPlayerIdx}>
                <SelectTrigger><SelectValue placeholder="-- Selecione o atleta --" /></SelectTrigger>
                <SelectContent>
                  {(actionTeam === "host" ? host : visitor)
                    .map((p, i) => ({ p, i }))
                    .filter(({ p }) => {
                      const q = actionSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (p.name || "").toLowerCase().includes(q)
                        || String(p.jersey_number ?? "").toLowerCase().includes(q);
                    })
                    .map(({ p, i }) => (
                      <SelectItem key={i} value={String(i)}>
                        {p.jersey_number ? `${p.jersey_number} · ` : ""}{p.name || "(sem nome)"}
                      </SelectItem>
                    ))}
                  {(actionTeam === "host" ? host : visitor).length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Nenhum atleta na equipe.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            {actionKind === "card" && (
              <div>
                <Label>Tipo de cartão</Label>
                <Select value={actionCard} onValueChange={(v) => setActionCard(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yellow">🟨 Amarelo</SelectItem>
                    <SelectItem value="red">🟥 Vermelho</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(false)}>Cancelar</Button>
            <Button onClick={applyAction} disabled={actionKind !== "foul" && actionPlayerIdx === ""}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Encerrar jogo</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            O jogo está sendo encerrado e a súmula será salva. Depois disso ela
            <b> não poderá mais ser alterada</b> (nem pelo link compartilhado).
            Deseja realmente encerrar?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleCloseGame} disabled={closing}>OK, encerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reabrir súmula</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Ao reabrir, a súmula poderá ser editada novamente. Após a data final do
            evento a reabertura não é permitida. Deseja realmente reabrir?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleReopen} disabled={closing}>
              OK, reabrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}


function TeamTable({
  title, players, onChange, onAdd, onRemove, showStudentAvatar,
}: {
  title: string; players: Player[];
  onChange: (i: number, k: keyof Player, v: any) => void;
  onAdd: () => void; onRemove: (i: number) => void;
  showStudentAvatar?: boolean;
}) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-sm">{title}</h3>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 w-10 text-center">Pres.</th>
              <th className="p-2 text-left w-10">Nº</th>
              <th className="p-2 text-left w-10">Foto</th>
              <th className="p-2 text-left">Atleta</th>
              <th className="p-2 w-12">Gols</th>
              <th className="p-2 w-12">CA</th>
              <th className="p-2 w-12">CV</th>
              <th className="p-2 w-12">Faltas</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={i} className={`border-t ${p.is_above_category ? "bg-destructive/10" : ""} ${p.present === false ? "opacity-60" : ""}`}>
                <td className="p-1 text-center">
                  <input type="checkbox" className="h-4 w-4 accent-emerald-600"
                    checked={p.present !== false}
                    onChange={(e) => onChange(i, "present", e.target.checked)} />
                </td>
                <td className="p-1">
                  <Input className="h-7 px-1 text-center" value={p.jersey_number ?? ""} onChange={(e) => onChange(i, "jersey_number", e.target.value)} />
                </td>
                <td className="p-1">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="h-7 w-7 rounded-full object-cover border" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-muted border shadow-inner" aria-hidden />
                  )}
                </td>
                <td className="p-1">
                  {p.student_id ? (
                    <span className="px-2 inline-flex items-center gap-2">
                      {p.name}
                      {p.is_above_category && p.category && (
                        <span className="text-[10px] font-semibold text-destructive uppercase">{p.category}</span>
                      )}
                    </span>
                  ) : (
                    <Input className="h-7" value={p.name} onChange={(e) => onChange(i, "name", e.target.value)} placeholder="Nome do atleta" />
                  )}
                </td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-muted/50" value={p.goals ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-yellow-100" value={p.yellow_cards ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-red-100" value={p.red_cards ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-muted/50" value={p.fouls ?? 0} /></td>
                <td className="p-1 text-center">
                  {!p.student_id && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onRemove(i)}><Trash2 className="h-3 w-3" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">Nenhum atleta. Adicione abaixo.</td></tr>
            )}

          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" onClick={onAdd}><Plus className="h-3 w-3 mr-1" />Adicionar Atleta</Button>
    </div>
  );
}

function PrintTeam({ title, players, isVisitor }: { title: string; players: Player[]; isVisitor?: boolean }) {
  return (
    <div>
      <div style={{ background: "#111", color: "#fff", padding: "4px 8px", fontWeight: 700, fontSize: 12 }}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "#eee" }}>
            <th style={thStyle}>Nº</th>
            <th style={thStyle}>Foto</th>
            <th style={{ ...thStyle, textAlign: "left" }}>Atleta</th>
            <th style={thStyle}>Gols</th>
            <th style={thStyle}>CA</th>
            <th style={thStyle}>CV</th>
          </tr>
        </thead>
        <tbody>
          {players.length === 0 ? (
            <tr><td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: "#666" }}>Sem atletas</td></tr>
          ) : players.map((p, i) => (
            <tr key={i} style={p.is_above_category ? { background: "#fde2e2" } : undefined}>
              <td style={{ ...tdStyle, textAlign: "center" }}>{p.jersey_number || "-"}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                {p.photo_url
                  ? <img src={p.photo_url} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", border: "1px solid #999" }} />
                  : <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#e5e5e5", border: "1px solid #bbb", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.25)", margin: "0 auto" }} />}
              </td>
              <td style={tdStyle}>
                {p.name}
                {p.is_above_category && p.category ? <span style={{ color: "#c00", fontSize: 9, marginLeft: 4, textTransform: "uppercase" }}>({p.category})</span> : null}
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}>{p.goals}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}>{p.yellow_cards}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}>{p.red_cards}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {isVisitor ? null : null}
    </div>
  );
}

const thStyle: React.CSSProperties = { border: "1px solid #999", padding: "3px 4px", fontSize: 10 };
const tdStyle: React.CSSProperties = { border: "1px solid #ccc", padding: "3px 4px", verticalAlign: "middle" };

function PrintableScoresheet(props: {
  tcName: string; champName: string; champCategory: string;
  matchDate: string; matchTime: string; location: string;
  host: Player[]; visitor: Player[]; visitorName: string;
  refereeName: string; refereeReport: string;
}) {
  const totalHost = props.host.reduce((a, p) => a + (p.goals || 0), 0);
  const totalVis = props.visitor.reduce((a, p) => a + (p.goals || 0), 0);
  return (
    <div className="print-only" aria-hidden>
      <style>{`
        @media screen { .print-only { display: none; } }
        @media print {
          @page { size: A4; margin: 12mm; }
          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }
          .print-only { position: absolute; inset: 0; display: block; color: #000; }
        }
      `}</style>
      <div style={{ padding: 8, fontFamily: "Arial, sans-serif" }}>
        <div style={{ textAlign: "center", borderBottom: "2px solid #000", paddingBottom: 6, marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>SÚMULA DE JOGO — {props.champName.toUpperCase()}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            {props.tcName} · Data: {props.matchDate} · Campo: {props.location} · Categoria: {props.champCategory || "-"} · Horário: {props.matchTime}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <PrintTeam title={`${props.tcName.toUpperCase()} (MANDANTE) — ${totalHost} gol(s)`} players={props.host} />
          <PrintTeam title={`${(props.visitorName || "VISITANTE").toUpperCase()} (VISITANTE) — ${totalVis} gol(s)`} players={props.visitor} isVisitor />
        </div>

        <div style={{ marginTop: 10, border: "1px solid #999", padding: 8, fontSize: 11 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>REGRAS ESTABELECIDAS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 2, columnGap: 12 }}>
            <div><b>Modalidade:</b> QUADRA</div>
            <div><b>Calçado:</b> Chuteira sem trava quando for salão</div>
            <div><b>Tempo:</b> 2x de 20 min</div>
            <div><b>Goleiro Acima:</b> Sim</div>
            <div><b>Feminino Acima:</b> Sim</div>
            <div><b>Atletas:</b> 5 linha (Max inscritos: 15)</div>
          </div>
          <div style={{ marginTop: 6, fontSize: 10, color: "#333" }}>
            Futsal: Amarelo = Advertência (2+1 Vermelho). Vermelho = Expulsão (2 min fora ou até sofrer gol, entra outro). 6ª Falta = Tiro Livre Direto.
          </div>
        </div>

        <div style={{ marginTop: 10, border: "1px solid #999", padding: 8, fontSize: 11, minHeight: 90 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>RELATÓRIO DO ÁRBITRO</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{props.refereeReport || "—"}</div>
        </div>

        <div style={{ marginTop: 30, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
          {[
            { label: "ASSINATURA ÁRBITRO", name: props.refereeName },
            { label: "ASSINATURA CAPITÃO MANDANTE", name: "" },
            { label: "ASSINATURA CAPITÃO VISITANTE", name: "" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ height: 40 }} />
              <div style={{ borderTop: "1px solid #000", paddingTop: 2, fontSize: 10 }}>{s.name || " "}</div>
              <div style={{ fontSize: 10, color: "#333" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
